import { useContext, useEffect, useMemo, useState } from "react";
import useModalClose from "../../hooks/useModalClose";
import Button from "../Reusables/Button/Button";
import Close from "../../assets/icons/close.png";
import { AppContext } from "../../Context/Context";
import PopupDatePicker from "../Reusables/DatePicker/PopupDatePicker";
import TimePicker from "../Reusables/TimePicker/TimePicker";
import moment, { min } from "moment";
import AlertMessage from "../Reusables/AlertMessage/AlertMessage";
import { getTimeBasedOnUserTime } from "../../util/helper/helperFunctions";
import { useDispatch, useSelector } from "react-redux";
import { setTimeMachineV2ModeIsEnabled } from "../../store/newTimeMachineSlice";
import { useLocation } from "react-router-dom";

const MODAL_ID = "time-machine-range-wrapper-id";

const TimeMachineRangePopup = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const {
    setTimeMachineTimeFrames,
    setOpenTimeMachineRangePopup,
    openTimeMachineRangePopup
  } = useContext(AppContext);

  const { timeMachineMaxTimeStamp } = useSelector(
    (state) => state.newTimeMachine
  );

  const [timeFrame, setTimeFrame] = useState({
    fromDate: "",
    toDate: "",
    fromTime: "",
    toTime: ""
  });
  const defaultValidationTime = {
    fromDate: true,
    toDate: true,
    fromTime: true,
    toTime: true
  };
  const [timeFrameValidation, setTimeFrameValidation] = useState(
    defaultValidationTime
  );
  const [isTimeFrameValid, setIsTimeFrameValid] = useState(false);
  const [alertMessageType, setAlertMessageType] = useState(null);
  const [timeRangeMinimumFromDate, setTimeRangeMinimumFromDate] = useState("");
  const [timeRangeMaximumFromDate, setTimeRangeMaximumFromDate] = useState("");
  const [timeRangeMinumumToDate, setTimeRangeMinumumToDate] = useState("");
  const [timeRangeMaximumToDate, setTimeRangeMaximumToDate] = useState("");
  const [minDateObj, setMinDateObj] = useState(null);
  const [maxDateObj, setMaxDateObj] = useState(null);

  const [timeFrameSelected, setTimeFrameSelected] = useState("");

  const enterTimeMachineMode = () => {
    dispatch(setTimeMachineV2ModeIsEnabled(true));
  };

  const resetAlertMessage = () => {
    setTimeout(() => {
      // setIsTimeFrameValid(true);
      setAlertMessageType(null);
    }, 30000);
  };

  const clearTimeFrame = () => {
    setValidateTimeFrame(defaultValidationTime);
    setMinAndMaxDateRange();
    setTimeFrameSelected("");
    setIsTimeFrameValid(false);
    setAlertMessageType(null);
  };

  const applyTimeFrame = () => {
    setValidateTimeFrame();

    if (validateTimeFrames()) {
      setTimeMachineTimeFrames({ ...timeFrame });
      enterTimeMachineMode();
      setOpenTimeMachineRangePopup(false);
      setIsTimeFrameValid(true);
    } else {
      const { fromDtTime, toDtTime } = getFromAndToTimeObj();
      setTimeFrameValidation((timeFrameValidation) => ({
        ...timeFrameValidation,
        fromTime: false
      }));
      setIsTimeFrameValid(false);
      if (
        timeFrame.fromDate == timeFrame.toDate &&
        timeFrame.fromTime == timeFrame.toTime
      ) {
        setAlertMessageType("equal-date-time");
      } else if (fromDtTime > toDtTime) {
        setAlertMessageType("date-time-overlap");
      } else if (isFromOrToTimeBeyondAvailableDateTime())
        setAlertMessageType("date-time-out-of-range");
      else {
        setAlertMessageType("warning");
      }
      resetAlertMessage();
    }
  };

  const getFromAndToTimeObj = () => {
    let fromDateTime = `${timeFrame.fromDate} ${timeFrame.fromTime || "00:00"}`;
    let toDateTime = `${timeFrame.toDate} ${timeFrame.toTime || "00:00"}`;
    let fromDtTime = new Date(fromDateTime).getTime();
    let toDtTime = new Date(toDateTime).getTime();
    return {
      fromDtTime,
      toDtTime
    };
  };

  const isFromOrToTimeBeyondAvailableDateTime = () => {
    let fromDateTime = `${timeFrame.fromDate} ${timeFrame.fromTime || "00:00"}`;
    let toDateTime = `${timeFrame.toDate} ${timeFrame.toTime || "00:00"}`;

    const fromDtTime = moment(fromDateTime);
    const toDtTime = moment(toDateTime);
    return minDateObj > fromDtTime || maxDateObj < toDtTime;
  };

  const validateTimeFrames = () => {
    const { fromDtTime, toDtTime } = getFromAndToTimeObj();
    if (
      timeFrame.fromDate == undefined ||
      timeFrame.toDate == undefined ||
      timeFrame.fromTime == undefined ||
      timeFrame.toTime == undefined ||
      timeFrame.fromDate == "" ||
      timeFrame.toDate == "" ||
      timeFrame.fromTime == "" ||
      timeFrame.toTime == "" ||
      (timeFrame.fromDate == timeFrame.toDate &&
        timeFrame.fromTime == timeFrame.toTime) ||
      fromDtTime > toDtTime ||
      isFromOrToTimeBeyondAvailableDateTime()
    ) {
      return false;
    } else {
      return true;
    }
  };

  const setValidateTimeFrame = () => {
    if (timeFrame.fromDate == "" || timeFrame.fromDate == undefined) {
      setTimeFrameValidation((validation) => ({
        ...validation,
        fromDate: false
      }));
    } else {
      setTimeFrameValidation((validation) => ({
        ...validation,
        fromDate: true
      }));
    }
    if (timeFrame.toDate == "" || timeFrame.toDate == undefined) {
      setTimeFrameValidation((validation) => ({
        ...validation,
        toDate: false
      }));
    } else {
      setTimeFrameValidation((validation) => ({ ...validation, toDate: true }));
    }
    if (timeFrame.fromTime == "" || timeFrame.fromTime == undefined) {
      setTimeFrameValidation((validation) => ({
        ...validation,
        fromTime: false
      }));
    } else {
      setTimeFrameValidation((validation) => ({
        ...validation,
        fromTime: true
      }));
    }
    if (timeFrame.toTime == "" || timeFrame.toTime == undefined) {
      setTimeFrameValidation((validation) => ({
        ...validation,
        toTime: false
      }));
    } else {
      setTimeFrameValidation((validation) => ({ ...validation, toTime: true }));
    }
  };

  const setEarlierTime = (min) => {
    let fromDateTimeObj = moment(maxDateObj).subtract(min, "minute");
    timeFrame.fromDate = fromDateTimeObj.format("YYYY-MM-DD");
    timeFrame.fromTime = fromDateTimeObj.format("HH:mm");
    timeFrame.toDate = maxDateObj.format("YYYY-MM-DD");
    timeFrame.toTime = maxDateObj.format("HH:mm");
    setTimeFrame(timeFrame);
  };
  const set30MinsTimeFrame = () => {
    setEarlierTime(30);
    setTimeFrameSelected("30");
    setIsTimeFrameValid(false);
    resetAlertMessage();
  };
  const set1hrTimeFrame = () => {
    setEarlierTime(60);
    setTimeFrameSelected("1");
    setIsTimeFrameValid(false);
    resetAlertMessage();
  };

  const set3hrTimeFrame = () => {
    setEarlierTime(180);
    setTimeFrameSelected("3");
    setIsTimeFrameValid(false);
    resetAlertMessage();
  };

  const setTodayTimeFrame = () => {
    // Calculate the currentDateTime from maxDateObj
    const currentDateTime = moment(maxDateObj); // Use maxDateObj as the base

    let fromDateTimeObj;

    // If current time is exactly midnight (00:00), subtract one day for fromDate
    if (currentDateTime.format("HH:mm") === "00:00") {
      fromDateTimeObj = moment(maxDateObj).subtract(1, "day").startOf("day"); // Start of previous day
      timeFrame.fromDate = fromDateTimeObj.format("YYYY-MM-DD");
      timeFrame.fromTime = "00:00";
      timeFrame.toDate = currentDateTime.format("YYYY-MM-DD");
      timeFrame.toTime = currentDateTime.format("HH:mm");
    } else {
      // If current time is not exactly midnight, subtract 'min' minutes from maxDateObj
      fromDateTimeObj = moment(maxDateObj).subtract(min, "minutes"); // Adjust for 'min' minutes
      timeFrame.fromDate = fromDateTimeObj.format("YYYY-MM-DD");
      timeFrame.fromTime = "00:00";
      timeFrame.toDate = currentDateTime.format("YYYY-MM-DD");
      timeFrame.toTime = currentDateTime.format("HH:mm");
    }

    // Set the time frame and update the selected time frame
    setTimeFrame(timeFrame);
    setTimeFrameSelected("today");

    // Set validity and alert message
    setIsTimeFrameValid(false);
    resetAlertMessage();
  };

  const setMinAndMaxDateRange = () => {
    let maximumDate = getTimeBasedOnUserTime(timeMachineMaxTimeStamp).format(
      "YYYY-MM-DD HH:mm:ss"
    );
    let maxDateObj = moment(maximumDate);
    let minDateObj = moment(maximumDate).subtract(5, "days");
    let lessOneDateObj = moment(maximumDate).subtract(1, "days");

    setTimeRangeMinimumFromDate(minDateObj.format("YYYY-MM-DD"));
    setTimeRangeMaximumFromDate(maxDateObj.format("YYYY-MM-DD"));

    if(!pathname.includes('new-time-machine')) {
      setTimeRangeMinumumToDate(minDateObj.format("YYYY-MM-DD"));
      setMinDateObj(minDateObj);
    }
    setTimeRangeMaximumToDate(maxDateObj.format("YYYY-MM-DD"));
    setMaxDateObj(maxDateObj);

    timeFrame.fromDate = lessOneDateObj.format("YYYY-MM-DD");
    timeFrame.fromTime = lessOneDateObj.format("HH:mm");
    timeFrame.toDate = maxDateObj.format("YYYY-MM-DD");
    timeFrame.toTime = maxDateObj.format("HH:mm");
    setTimeFrame(timeFrame);
  };

  useEffect(() => {
    if (timeFrame.fromDate) {
      setTimeRangeMinumumToDate(timeFrame.fromDate);
    } else {
      setTimeRangeMinumumToDate("");
    }

    if (timeFrame.toDate) {
      setTimeRangeMaximumFromDate(timeFrame.toDate);
    } else {
      setTimeRangeMaximumFromDate("");
    }
  }, [timeFrame.fromDate, timeFrame.toDate]);

  useEffect(() => {
    if (timeMachineMaxTimeStamp) setMinAndMaxDateRange();
  }, [timeMachineMaxTimeStamp]);

  useEffect(() => {
    if (
      timeFrame.fromDate &&
      timeFrame.toDate &&
      timeFrame.fromTime &&
      timeFrame.toTime
    ) {
      setIsTimeFrameValid(false);
    }
  }, [
    timeFrame.fromDate,
    timeFrame.toDate,
    timeFrame.fromTime,
    timeFrame.toTime
  ]);

  const onChangeHandler = (e) => {
    setTimeFrameSelected("");
  };

  const renderAlertMessage = useMemo(() => {
    if (alertMessageType !== null) {
      let alertProps = {
        heading: "",
        description: "",
        type: ""
      };
      switch (alertMessageType) {
        case "warning":
          alertProps.heading = "Invalid Time Range";
          alertProps.description = "Time Range must be greater than 15 mins";
          alertProps.type = "warning";
          break;
        case "restricted":
          alertProps.heading = "Time Range Invalid";
          alertProps.description = "Time Range is only available for 6 hours";
          alertProps.type = "warning";
          break;
        case "equal-date-time":
          alertProps.heading = "Time Range Invalid";
          alertProps.description =
            "From date time & to date time should not be equal";
          alertProps.type = "warning";
          break;
        case "date-time-overlap":
          alertProps.heading = "Time Range Invalid";
          alertProps.description = "Selected date time should not be overlap";
          alertProps.type = "warning";
          break;

        case "date-time-out-of-range":
          alertProps.heading = "Time Range Invalid";
          alertProps.description =
            "Selected date time should be in available date time range";
          alertProps.type = "warning";
          break;

        default: {
          alertProps = null;
        }
      }
      return (
        alertProps && (
          <AlertMessage
            heading={alertProps.heading}
            description={alertProps.description}
            type={alertProps.type}
            setAlertMessageType={setAlertMessageType}
          />
        )
      );
    }
  }, [alertMessageType]);

  useModalClose(MODAL_ID, () => setOpenTimeMachineRangePopup(false));

  return (
    openTimeMachineRangePopup && (
      <div id={MODAL_ID} className="time-range-wrapper">
        <div className="time-range-container">
          <div className="time-range-top-section">
            <p>Time Range</p>
            <img
              onClick={() => setOpenTimeMachineRangePopup(false)}
              className="time-range-popup-close-btn"
              src={Close}
              alt="close_icon"
            />
          </div>
          <div className="time-range-middle-section">
            <div className="picker-section">
              <div className="time-range-left-panel">
                From
                <div className="time-range-pickers">
                  <PopupDatePicker
                    validation={timeFrameValidation.fromDate}
                    date={timeFrame.fromDate}
                    getDate={setTimeFrame}
                    name="fromDate"
                    min={timeRangeMinimumFromDate}
                    max={timeRangeMaximumFromDate}
                    onChange={onChangeHandler}
                  />
                  <TimePicker
                    validation={timeFrameValidation.fromTime}
                    time={timeFrame.fromTime}
                    getTime={setTimeFrame}
                    name="fromTime"
                    onChange={onChangeHandler}
                  />
                </div>
              </div>
              <div className="time-range-right-panel">
                To
                <div className="time-range-pickers">
                  <PopupDatePicker
                    validation={timeFrameValidation.toDate}
                    date={timeFrame.toDate}
                    getDate={setTimeFrame}
                    name="toDate"
                    min={timeRangeMinumumToDate}
                    max={timeRangeMaximumToDate}
                    onChange={onChangeHandler}
                  />
                  <TimePicker
                    validation={timeFrameValidation.toTime}
                    time={timeFrame.toTime}
                    getTime={setTimeFrame}
                    name="toTime"
                    onChange={onChangeHandler}
                  />
                </div>
              </div>
            </div>
            <div className="time-range-info">
              <p>Please enter the time as per your system time format.</p>

              {minDateObj && maxDateObj && (
                <p className="available-date-time">
                  <span>Available Data - </span> <label>From:</label>{" "}
                  {minDateObj.format("MM/DD/YYYY HH:mm")}{" "}
                  <label className="toTime">To:</label>{" "}
                  {maxDateObj.format("MM/DD/YYYY HH:mm")}
                </p>
              )}
            </div>

            <div className="time-frame-section">
              <Button
                onClick={() => set30MinsTimeFrame()}
                placeholder="Last 30 mins"
                btnClass={`time-frame-btn ${
                  timeFrameSelected == "30" ? "time-frame-btn-selected" : ""
                }`}
              ></Button>
              <Button
                onClick={() => set1hrTimeFrame()}
                placeholder="Last 1 hr"
                btnClass={`time-frame-btn ${
                  timeFrameSelected == "1" ? "time-frame-btn-selected" : ""
                }`}
              ></Button>
              <Button
                onClick={() => set3hrTimeFrame()}
                placeholder="Last 3 hr"
                btnClass={`time-frame-btn ${
                  timeFrameSelected == "3" ? "time-frame-btn-selected" : ""
                }`}
              ></Button>
              <Button
                onClick={() => setTodayTimeFrame()}
                placeholder="Today"
                btnClass={`time-frame-btn ${
                  timeFrameSelected == "today" ? "time-frame-btn-selected" : ""
                }`}
              ></Button>
            </div>
          </div>
          <div className="time-range-bottom-section">
            <Button
              onClick={() => clearTimeFrame()}
              placeholder="Reset"
              btnClass="time-range-discard-btn time-range-btn"
            ></Button>
            <Button
              onClick={() => applyTimeFrame()}
              placeholder="Apply"
              btnClass={`time-range-confirm-btn time-range-btn ${
                isTimeFrameValid ? "time-range-invalid" : ""
              }`}
              disabled={isTimeFrameValid}
            ></Button>
          </div>
          {renderAlertMessage}
        </div>
      </div>
    )
  );
};

export default TimeMachineRangePopup;
